// Pure helpers for the BFM multi-city stepper. Every "option" and "total" here comes from a
// complete BFM-priced itinerary — sector prices are never added together on the client.

export const paxCount = (p = {}) => (p.adults || 0) + (p.youth || 0) + (p.children || 0) + (p.infants || 0) || 1;

export const perPerson = (itin, pax) => (itin ? Math.round(itin.price / paxCount(pax)) : 0);

export const matchesSelection = (itin, selectedKeys) => selectedKeys.every((k, i) => itin.legs[i]?.key === k);

export const compatible = (itineraries, selectedKeys) => itineraries.filter((it) => matchesSelection(it, selectedKeys));

// Recommended = cheapest itinerary that keeps ONE airline on every sector; otherwise the cheapest valid one.
export const recommendedOf = (itineraries) => {
  if (!itineraries.length) return null;
  const sorted = [...itineraries].sort((a, b) => a.price - b.price);
  return sorted.find((it) => it.same_airline) || sorted[0];
};

// Distinct flights for sector `step`, given what is already chosen. Each option carries the
// recommended itinerary for that flight (same-airline first) and the cheapest total containing it.
export const optionsForStep = (itineraries, selectedKeys, step) => {
  const pool = compatible(itineraries, selectedKeys);
  const overall = recommendedOf(pool);
  const groups = new Map();
  pool.forEach((it) => {
    const leg = it.legs[step];
    if (!leg) return;
    const g = groups.get(leg.key) || { leg, itineraries: [] };
    g.itineraries.push(it);
    groups.set(leg.key, g);
  });
  const options = [...groups.values()].map((g) => {
    const primary = recommendedOf(g.itineraries);
    const cheapest = g.itineraries.reduce((m, it) => (it.price < m.price ? it : m), g.itineraries[0]);
    return {
      key: g.leg.key,
      leg: g.leg,
      primary,
      cheapest,
      count: g.itineraries.length,
      isRecommended: !!overall && g.itineraries.some((it) => it.key === overall.key),
      saving: overall ? Math.round(overall.price - primary.price) : 0,
    };
  });
  options.sort((a, b) => (b.isRecommended - a.isRecommended) || (a.primary.price - b.primary.price) || (a.cheapest.price - b.cheapest.price));
  return { options, recommended: overall, pool };
};

export const finalItinerary = (itineraries, selectedKeys, legCount) => {
  if (selectedKeys.length < legCount) return null;
  const pool = compatible(itineraries, selectedKeys);
  return pool.length ? pool.reduce((m, it) => (it.price < m.price ? it : m), pool[0]) : null;
};
